import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Package, Plus, Search, Archive as ArchiveIcon, RotateCcw, Loader2, AlertTriangle,
  CalendarClock, ArrowUpRight, ArrowDownRight, ClipboardList, X, Trash2, FlaskConical,
} from "lucide-react";

const DOSAGE_FORMS = ["Injection", "Oral solution", "Capsule", "Tablet", "Cream", "Ointment", "Suppository", "Sublingual troche", "Nasal spray", "Ophthalmic drops", "Powder"];
const UNITS = ["each", "vial", "mL", "g", "mg", "bottle", "box", "kit"];
const CATEGORIES = ["Active ingredient", "Excipient", "Finished good", "Supply", "Controlled substance", "Reference standard"];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr + "T00:00:00Z");
    return Math.floor((d - new Date()) / 86400000);
  } catch { return null; }
}

function ExpiryBadge({ dateStr }) {
  const d = daysUntil(dateStr);
  if (d === null) return <span className="text-slate-300">—</span>;
  const cls = d < 0 ? "bg-rose-100 text-rose-800 border-rose-300"
    : d <= 30 ? "bg-orange-100 text-orange-800 border-orange-300"
    : d <= 60 ? "bg-amber-50 text-amber-800 border-amber-300"
    : "bg-slate-50 text-slate-600 border-slate-200";
  const lbl = d < 0 ? `expired ${-d}d ago` : `${d}d`;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{dateStr} · {lbl}</span>;
}

function ReorderPointInput({ item, onSaved }) {
  const [val, setVal] = useState(String(item.reorder_point || ""));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(String(item.reorder_point || "")); }, [item.reorder_point]);
  const save = async () => {
    const next = Number(val || 0);
    if (next === Number(item.reorder_point || 0)) return;
    setSaving(true);
    try {
      // Send the whole item so PUT clean_payload keeps other fields intact
      await api.put(`/inventory/${item.id}`, { ...item, reorder_point: next });
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      setVal(String(item.reorder_point || ""));
    } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center justify-end gap-1">
      <input data-testid={`inv-reorder-${item.id}`} type="number" step="any" value={val}
        onChange={(e) => setVal(e.target.value)} onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        className={`w-16 h-7 text-xs font-mono text-right px-1.5 rounded border ${
          saving ? "border-pink-300 bg-pink-50" : "border-slate-200 hover:border-slate-300 focus:border-pink-700"
        } focus:outline-none focus:ring-1 focus:ring-pink-700`}
        placeholder="—" />
      <span className="text-[9px] text-slate-400">{item.unit}</span>
    </div>
  );
}

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, low_stock: 0, expiring_60d: 0 });
  const [q, setQ] = useState("");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [category, setCategory] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);         // full item
  const [adjustFor, setAdjustFor] = useState(null);     // {item, lot}
  const [seeding, setSeeding] = useState(false);

  const seedTestData = async () => {
    if (!confirm("Seed a realistic pharmacy inventory catalog (APIs, vials, stoppers, caps, filters, disinfection agents) with test lot numbers and expiries?\n\nAlready-existing SKUs are skipped — this is safe to click multiple times.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/inventory/seed-test-data");
      toast.success(`Seeded ${data.created} new SKUs · ${data.skipped} skipped (already existed)`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
    finally { setSeeding(false); }
  };

  const reload = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/inventory", {
        params: { q, category, low_stock: lowStockOnly, include_archived: showArchived },
      });
      setItems(data.items || []);
      setCounts(data.counts || { total: 0, low_stock: 0, expiring_60d: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load inventory");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [showArchived, lowStockOnly, category]);
  useEffect(() => { const t = setTimeout(reload, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const archive = async (id) => {
    if (!confirm("Archive this inventory item? Adjustment history stays intact.")) return;
    try { await api.delete(`/inventory/${id}`); toast.success("Archived"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
  };
  const restore = async (id) => {
    try { await api.post(`/inventory/${id}/restore`); toast.success("Restored"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/inventory/${id}`);
      setEditing(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load item"); }
  };

  const filteredItems = useMemo(() => items, [items]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-7 h-7 text-pink-700" /> Inventory
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Lot-tracked stock room · openFDA NDC lookup · low-stock &amp; expiry alerts · full audit trail.
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button data-testid="inventory-seed-btn" onClick={seedTestData} disabled={seeding}
              variant="outline" size="sm" className="h-10 gap-1.5 text-xs border-amber-300 text-amber-800 hover:bg-amber-50">
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />}
              Seed test inventory
            </Button>
            <Button data-testid="inventory-new-btn" onClick={() => setCreating(true)}
              className="bg-pink-700 hover:bg-pink-800 gap-1.5">
              <Plus className="w-4 h-4" /> New Item
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Card className="p-4 border-slate-200">
          <div className="text-xs text-slate-500 uppercase">Active SKUs</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{counts.total}</div>
        </Card>
        <Card className={`p-4 border ${counts.low_stock > 0 ? "border-rose-300 bg-rose-50" : "border-slate-200"}`}>
          <div className="text-xs text-slate-500 uppercase flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Low stock</div>
          <div className={`text-2xl font-bold mt-1 ${counts.low_stock > 0 ? "text-rose-700" : "text-slate-800"}`}>{counts.low_stock}</div>
        </Card>
        <Card className={`p-4 border ${counts.expiring_60d > 0 ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}>
          <div className="text-xs text-slate-500 uppercase flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Expiring ≤ 60d</div>
          <div className={`text-2xl font-bold mt-1 ${counts.expiring_60d > 0 ? "text-amber-800" : "text-slate-800"}`}>{counts.expiring_60d}</div>
        </Card>
      </div>

      <Card className="p-3 mb-4 border-slate-200 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="inventory-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, NDC, brand, generic, manufacturer…" className="pl-9 h-9 text-sm" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="inventory-category-filter"
          className="h-9 text-sm px-2 rounded-md border border-slate-200">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button data-testid="inventory-lowstock-toggle" onClick={() => setLowStockOnly((s) => !s)}
          className={`text-xs px-3 py-1 rounded-full border font-medium ${
            lowStockOnly ? "bg-rose-700 text-white border-rose-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}>Low stock only</button>
        <button data-testid="inventory-archived-toggle" onClick={() => setShowArchived((s) => !s)}
          className={`text-xs px-3 py-1 rounded-full border font-medium ${
            showArchived ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}>{showArchived ? "Showing archived" : "Show archived"}</button>
        <span className="text-xs text-slate-500 ml-auto">{filteredItems.length} item{filteredItems.length === 1 ? "" : "s"}</span>
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-sm text-slate-500 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            No inventory items{q || category || lowStockOnly ? " match your filters" : ""}. {isAdmin && !q && "Click New Item to add your first SKU."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="inventory-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name / NDC</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">On hand</TableHead>
                  <TableHead className="text-right">Reorder pt</TableHead>
                  <TableHead>Earliest expiry</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((it) => (
                  <TableRow key={it.id} data-testid={`inventory-row-${it.id}`}
                    className={it.is_low_stock ? "bg-rose-50/40" : ""}>
                    <TableCell>
                      <button onClick={() => openEdit(it.id)}
                        data-testid={`inventory-open-${it.id}`}
                        className="text-left font-medium text-slate-800 hover:text-pink-700 hover:underline">
                        {it.name}
                      </button>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {it.ndc && <span className="font-mono">NDC {it.ndc}</span>}
                        {it.brand_name && <span>{it.ndc ? " · " : ""}{it.brand_name}</span>}
                        {it.strength && <span> · {it.strength}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-700">{it.category || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      <span className={it.is_low_stock ? "text-rose-700 font-semibold" : "text-slate-800"}>
                        {it.total_quantity}
                      </span>
                      <span className="text-[10px] text-slate-500 ml-1">{it.unit}</span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-500">
                      {isAdmin ? (
                        <ReorderPointInput item={it} onSaved={reload} />
                      ) : (
                        it.reorder_point || <span className="text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell><ExpiryBadge dateStr={it.earliest_expiration} /></TableCell>
                    <TableCell>
                      {it.is_low_stock ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-rose-100 text-rose-800 border-rose-300">low stock</span>
                      ) : it.status === "archived" ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-300">archived</span>
                      ) : (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-300">active</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {isAdmin && it.status !== "archived" && (
                        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-slate-500 hover:text-rose-700"
                          data-testid={`inventory-archive-${it.id}`} onClick={() => archive(it.id)}>
                          <ArchiveIcon className="w-3 h-3" /> Archive
                        </Button>
                      )}
                      {isAdmin && it.status === "archived" && (
                        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
                          data-testid={`inventory-restore-${it.id}`} onClick={() => restore(it.id)}>
                          <RotateCcw className="w-3 h-3" /> Restore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <NewItemDialog open={creating} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />
      {editing && (
        <ItemDetailDialog item={editing} isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onChanged={async () => {
            const { data } = await api.get(`/inventory/${editing.id}`);
            setEditing(data);
            reload();
          }}
          onAdjust={(lot) => setAdjustFor({ item: editing, lot })}
        />
      )}
      {adjustFor && (
        <AdjustDialog data={adjustFor} onClose={() => setAdjustFor(null)}
          onSaved={async () => {
            const id = adjustFor.item.id;
            setAdjustFor(null);
            const { data } = await api.get(`/inventory/${id}`);
            setEditing(data);
            reload();
          }} />
      )}
    </div>
  );
}

// -------------------------------------------------- New Item dialog with NDC lookup
function NewItemDialog({ open, onClose, onSaved }) {
  const empty = {
    name: "", ndc: "", brand_name: "", generic_name: "", manufacturer: "",
    dosage_form: "", strength: "", package_description: "", category: "",
    unit: "each", reorder_point: 0, par_level: 0,
    storage_conditions: "", notes: "",
    lots: [{ lot_number: "", expiration_date: "", quantity: 0, cost_per_unit: 0, received_date: new Date().toISOString().slice(0, 10) }],
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [ndcLookingUp, setNdcLookingUp] = useState(false);
  useEffect(() => { if (open) setForm(empty); /* eslint-disable-next-line */ }, [open]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setLot = (i, k, v) => setForm((p) => {
    const lots = [...p.lots]; lots[i] = { ...lots[i], [k]: v }; return { ...p, lots };
  });
  const addLot = () => setForm((p) => ({ ...p, lots: [...p.lots, { lot_number: "", expiration_date: "", quantity: 0, cost_per_unit: 0, received_date: new Date().toISOString().slice(0, 10) }] }));
  const removeLot = (i) => setForm((p) => ({ ...p, lots: p.lots.filter((_, idx) => idx !== i) }));

  const lookupNdc = async () => {
    if (!form.ndc.trim()) return toast.error("Enter an NDC first");
    setNdcLookingUp(true);
    try {
      const { data } = await api.get("/openfda/ndc", { params: { ndc: form.ndc.trim() } });
      setForm((p) => ({
        ...p,
        name: p.name || `${data.brand_name || data.generic_name || "Drug"} ${data.strength || ""}`.trim(),
        ndc: data.ndc || p.ndc,
        brand_name: data.brand_name || p.brand_name,
        generic_name: data.generic_name || p.generic_name,
        manufacturer: data.manufacturer || p.manufacturer,
        dosage_form: data.dosage_form || p.dosage_form,
        strength: data.strength || p.strength,
        package_description: data.package_description || p.package_description,
      }));
      toast.success(`Found: ${data.brand_name || data.generic_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally { setNdcLookingUp(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Item name required");
    setSaving(true);
    try {
      await api.post("/inventory", form);
      toast.success("Item created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Inventory Item</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Card className="p-3 border-pink-200 bg-pink-50/40">
            <div className="text-xs font-semibold text-pink-900 mb-2 uppercase">openFDA NDC lookup</div>
            <div className="flex items-center gap-2">
              <Input data-testid="ndc-lookup-input" value={form.ndc}
                onChange={(e) => set("ndc", e.target.value)}
                placeholder="e.g. 0002-1433" className="h-9 text-sm font-mono max-w-xs" />
              <Button data-testid="ndc-lookup-btn" onClick={lookupNdc} disabled={ndcLookingUp}
                variant="outline" size="sm" className="h-9 gap-1">
                {ndcLookingUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                Look up
              </Button>
              <span className="text-[11px] text-slate-500">Auto-fills name, manufacturer, dosage form, strength.</span>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Item name *</label>
              <Input data-testid="inv-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-9 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Category</label>
              <Input data-testid="inv-category" list="inv-cat" value={form.category} onChange={(e) => set("category", e.target.value)} className="h-9 text-sm" />
              <datalist id="inv-cat">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Brand name</label>
              <Input value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} className="h-9 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Generic name</label>
              <Input value={form.generic_name} onChange={(e) => set("generic_name", e.target.value)} className="h-9 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Manufacturer</label>
              <Input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} className="h-9 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Dosage form</label>
              <Input list="inv-df" value={form.dosage_form} onChange={(e) => set("dosage_form", e.target.value)} className="h-9 text-sm" />
              <datalist id="inv-df">{DOSAGE_FORMS.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Strength</label>
              <Input value={form.strength} onChange={(e) => set("strength", e.target.value)} className="h-9 text-sm" placeholder="e.g. 5 mg/mL" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Unit</label>
              <Input list="inv-u" value={form.unit} onChange={(e) => set("unit", e.target.value)} className="h-9 text-sm" />
              <datalist id="inv-u">{UNITS.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Reorder point</label>
              <Input data-testid="inv-reorder" type="number" step="any" value={form.reorder_point}
                onChange={(e) => set("reorder_point", Number(e.target.value))} className="h-9 text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Par level</label>
              <Input type="number" step="any" value={form.par_level}
                onChange={(e) => set("par_level", Number(e.target.value))} className="h-9 text-sm font-mono" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] text-slate-500 uppercase">Package description</label>
              <Input value={form.package_description} onChange={(e) => set("package_description", e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] text-slate-500 uppercase">Storage conditions</label>
              <Input value={form.storage_conditions} onChange={(e) => set("storage_conditions", e.target.value)} className="h-9 text-sm" placeholder="e.g. Refrigerated 2–8 °C" />
            </div>
          </div>

          <Card className="p-3 border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-800">Initial lots</div>
              <Button size="sm" variant="outline" onClick={addLot} className="h-7 gap-1 text-xs"><Plus className="w-3 h-3" /> Add lot</Button>
            </div>
            <div className="space-y-2">
              {form.lots.map((lot, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                  <Input placeholder="Lot #" value={lot.lot_number} onChange={(e) => setLot(i, "lot_number", e.target.value)} className="col-span-3 h-8 text-xs font-mono" />
                  <Input type="date" value={lot.expiration_date} onChange={(e) => setLot(i, "expiration_date", e.target.value)} className="col-span-3 h-8 text-xs" />
                  <Input type="number" step="any" placeholder="Qty" value={lot.quantity} onChange={(e) => setLot(i, "quantity", Number(e.target.value))} className="col-span-2 h-8 text-xs font-mono" />
                  <Input type="number" step="any" placeholder="Cost / unit" value={lot.cost_per_unit} onChange={(e) => setLot(i, "cost_per_unit", Number(e.target.value))} className="col-span-3 h-8 text-xs font-mono" />
                  <button onClick={() => removeLot(i)} className="col-span-1 text-rose-500 hover:text-rose-700 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </Card>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="inv-create-save" onClick={save} disabled={saving} className="bg-pink-700 hover:bg-pink-800 gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------- Item detail with lots + adjustments
function ItemDetailDialog({ item, isAdmin, onClose, onChanged, onAdjust }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(item);
  useEffect(() => setForm(item), [item]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/inventory/${item.id}`, form);
      toast.success("Saved");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Package className="w-5 h-5 text-pink-700" /> {form.name}</span>
            <button data-testid="inv-detail-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <Metric label="On hand" value={`${item.total_quantity} ${item.unit}`} tone={item.is_low_stock ? "rose" : "slate"} />
            <Metric label="Reorder pt" value={item.reorder_point || "—"} />
            <Metric label="Par level" value={item.par_level || "—"} />
            <Metric label="Earliest expiry" value={item.earliest_expiration || "—"} />
          </div>

          {/* Edit basics */}
          <Card className="p-3 border-slate-200">
            <div className="text-sm font-semibold text-slate-800 mb-2">Item details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name" value={form.name} onChange={(v) => set("name", v)} readonly={!isAdmin} />
              <Field label="NDC" value={form.ndc} onChange={(v) => set("ndc", v)} readonly={!isAdmin} mono />
              <Field label="Brand" value={form.brand_name} onChange={(v) => set("brand_name", v)} readonly={!isAdmin} />
              <Field label="Generic" value={form.generic_name} onChange={(v) => set("generic_name", v)} readonly={!isAdmin} />
              <Field label="Manufacturer" value={form.manufacturer} onChange={(v) => set("manufacturer", v)} readonly={!isAdmin} />
              <Field label="Category" value={form.category} onChange={(v) => set("category", v)} readonly={!isAdmin} />
              <Field label="Dosage form" value={form.dosage_form} onChange={(v) => set("dosage_form", v)} readonly={!isAdmin} />
              <Field label="Strength" value={form.strength} onChange={(v) => set("strength", v)} readonly={!isAdmin} />
              <Field label="Unit" value={form.unit} onChange={(v) => set("unit", v)} readonly={!isAdmin} />
              <Field label="Package description" value={form.package_description} onChange={(v) => set("package_description", v)} readonly={!isAdmin} />
              <Field label="Reorder point" value={form.reorder_point} onChange={(v) => set("reorder_point", Number(v))} readonly={!isAdmin} type="number" />
              <Field label="Par level" value={form.par_level} onChange={(v) => set("par_level", Number(v))} readonly={!isAdmin} type="number" />
              <div className="md:col-span-2">
                <Field label="Storage conditions" value={form.storage_conditions} onChange={(v) => set("storage_conditions", v)} readonly={!isAdmin} />
              </div>
            </div>
            {isAdmin && (
              <div className="flex justify-end mt-3">
                <Button data-testid="inv-detail-save" onClick={save} disabled={saving} className="bg-pink-700 hover:bg-pink-800 gap-1.5 h-8 text-xs">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save changes
                </Button>
              </div>
            )}
          </Card>

          {/* Lots table */}
          <Card className="p-3 border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-800">Lots on hand</div>
              {isAdmin && (
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
                  data-testid="inv-receive-btn"
                  onClick={() => onAdjust({ id: "", lot_number: "", expiration_date: "", quantity: 0, __new: true })}>
                  <ArrowDownRight className="w-3 h-3 text-emerald-600" /> Receive new lot
                </Button>
              )}
            </div>
            {(item.lots || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">No lots yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Lot #</TableHead>
                    <TableHead>Expiration</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Cost/unit</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {item.lots.map((lot) => (
                    <TableRow key={lot.id} data-testid={`inv-lot-${lot.id}`}>
                      <TableCell className="font-mono text-xs">{lot.lot_number || <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell><ExpiryBadge dateStr={lot.expiration_date} /></TableCell>
                      <TableCell className="text-right font-mono text-sm">{lot.quantity} <span className="text-[10px] text-slate-500">{item.unit}</span></TableCell>
                      <TableCell className="text-right font-mono text-xs text-slate-500">${(lot.cost_per_unit || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{lot.received_date || <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-xs text-slate-500">{lot.source || <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs"
                            data-testid={`inv-adjust-${lot.id}`} onClick={() => onAdjust(lot)}>
                            <ArrowUpRight className="w-3 h-3" /> Adjust
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          {/* Adjustment history */}
          <Card className="p-3 border-slate-200">
            <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1"><ClipboardList className="w-4 h-4 text-slate-500" /> Adjustment history</div>
            {(item.adjustments || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">No adjustments yet.</p>
            ) : (
              <div className="space-y-1">
                {item.adjustments.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-xs text-slate-700 py-1 border-b border-slate-100 last:border-b-0">
                    <span className="text-[10px] text-slate-500 font-mono w-32">{(a.ts || "").slice(0, 16).replace("T", " ")}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                      a.adjustment_type === "receive" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                      : a.adjustment_type === "use" ? "bg-sky-50 text-sky-800 border-sky-300"
                      : a.adjustment_type === "waste" ? "bg-rose-50 text-rose-800 border-rose-300"
                      : a.adjustment_type === "recount" ? "bg-amber-50 text-amber-800 border-amber-300"
                      : "bg-slate-50 text-slate-800 border-slate-300"
                    }`}>{a.adjustment_type}</span>
                    <span className="font-mono">{a.quantity_delta > 0 ? "+" : ""}{a.quantity_delta}</span>
                    <span className="text-slate-500 flex-1 truncate">{a.reason || ""} {a.notes ? `· ${a.notes}` : ""}</span>
                    <span className="text-[10px] text-slate-400">{a.actor_email}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value, tone = "slate" }) {
  const cls = tone === "rose" ? "border-rose-300 bg-rose-50 text-rose-800" : "border-slate-200 text-slate-800";
  return (
    <Card className={`p-2 border ${cls}`}>
      <div className="text-[10px] text-slate-500 uppercase">{label}</div>
      <div className="text-sm font-bold mt-0.5">{value}</div>
    </Card>
  );
}

function Field({ label, value, onChange, readonly, type = "text", mono = false }) {
  return (
    <div>
      <label className="text-[10px] text-slate-500 uppercase">{label}</label>
      <Input value={value ?? ""} type={type} onChange={(e) => onChange(e.target.value)} disabled={readonly}
        className={`h-8 text-xs ${mono ? "font-mono" : ""} disabled:bg-slate-50`} />
    </div>
  );
}

// -------------------------------------------------- Adjust dialog (receive / use / waste / recount)
function AdjustDialog({ data, onClose, onSaved }) {
  const { item, lot } = data;
  const isNew = lot.__new;
  const [type, setType] = useState(isNew ? "receive" : "use");
  const [qty, setQty] = useState(0);
  const [newQty, setNewQty] = useState(lot.quantity || 0);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [lotNum, setLotNum] = useState(lot.lot_number || "");
  const [exp, setExp] = useState(lot.expiration_date || "");
  const [cost, setCost] = useState(lot.cost_per_unit || 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { adjustment_type: type, lot_id: lot.id || "", reason, notes };
      if (type === "receive") {
        payload.quantity = qty;
        if (isNew || !lot.id) {
          payload.lot_number = lotNum; payload.expiration_date = exp; payload.cost_per_unit = cost;
        }
      } else if (type === "recount") {
        payload.new_quantity = newQty;
      } else {
        payload.quantity = qty;
      }
      await api.post(`/inventory/${item.id}/adjust`, payload);
      toast.success("Adjustment logged");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Adjustment failed"); }
    finally { setSaving(false); }
  };

  const TYPES = isNew
    ? [{ v: "receive", label: "Receive new lot" }]
    : [
        { v: "use", label: "Use (subtract)" },
        { v: "waste", label: "Waste / discard" },
        { v: "receive", label: "Receive more" },
        { v: "recount", label: "Recount" },
      ];

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Adjust stock — {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!isNew && (
            <div className="text-xs text-slate-500">
              Lot <span className="font-mono text-slate-700">{lot.lot_number || "(no #)"}</span> · exp {lot.expiration_date || "—"} · on hand <span className="font-mono text-slate-800">{lot.quantity} {item.unit}</span>
            </div>
          )}
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Adjustment type</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {TYPES.map((t) => (
                <button key={t.v} onClick={() => setType(t.v)} data-testid={`adj-type-${t.v}`}
                  className={`text-xs px-2 py-1 rounded-full border ${
                    type === t.v ? "bg-pink-700 text-white border-pink-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}>{t.label}</button>
              ))}
            </div>
          </div>

          {isNew && type === "receive" && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-slate-500 uppercase">Lot number</label>
                <Input data-testid="adj-lot-num" value={lotNum} onChange={(e) => setLotNum(e.target.value)} className="h-8 text-xs font-mono" /></div>
              <div><label className="text-[10px] text-slate-500 uppercase">Expiration</label>
                <Input type="date" value={exp} onChange={(e) => setExp(e.target.value)} className="h-8 text-xs" /></div>
              <div><label className="text-[10px] text-slate-500 uppercase">Cost / unit</label>
                <Input type="number" step="any" value={cost} onChange={(e) => setCost(Number(e.target.value))} className="h-8 text-xs font-mono" /></div>
            </div>
          )}

          {type === "recount" ? (
            <div>
              <label className="text-[10px] text-slate-500 uppercase">New quantity on hand</label>
              <Input data-testid="adj-new-qty" type="number" step="any" value={newQty}
                onChange={(e) => setNewQty(Number(e.target.value))} className="h-9 text-sm font-mono" />
              <p className="text-[11px] text-slate-500 mt-1">Delta will be logged automatically ({(newQty - (lot.quantity || 0)).toFixed(2)}).</p>
            </div>
          ) : (
            <div>
              <label className="text-[10px] text-slate-500 uppercase">
                Quantity {type === "receive" ? "received" : type === "waste" ? "wasted" : type === "transfer" ? "transferred" : "used"} ({item.unit})
              </label>
              <Input data-testid="adj-qty" type="number" step="any" value={qty}
                onChange={(e) => setQty(Number(e.target.value))} className="h-9 text-sm font-mono" />
            </div>
          )}

          <div>
            <label className="text-[10px] text-slate-500 uppercase">Reason</label>
            <Input data-testid="adj-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={type === "receive" ? "e.g. PO #1234" : type === "waste" ? "e.g. broken vial" : type === "use" ? "e.g. compounded batch VER-2026-001" : ""}
              className="h-9 text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Notes</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-9 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="adj-save" onClick={save} disabled={saving} className="bg-pink-700 hover:bg-pink-800 gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Log adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
